// @flow
import React, { Component } from 'react';
import { loadPaymentWidget, ANONYMOUS } from '@tosspayments/payment-widget-sdk';
import Circles from 'components/common/Loaders/Circles';
import OrderDetailTemplate from 'components/order/OrderDetailTemplate';
import type { State } from 'store';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { BaseActions, CartActions, OrdersActions } from '../../store/actionCreators';
import axios from '../../lib/defaultClient';

type Props = {
  order: any,
  logged: boolean,
};

class OrderDetailTemplateContainer extends Component<Props> {
  state = {
    paymentMethod: null,
    cancelOrder: false,
    reason: '',
    isRefundCancellation: false,
    refundCancellationReason: '',
    initLoading: true,
    paymentWidget: null,
    paymentMethodsWidget: null,
    paymentAgreement: null,
  };

  initialize = async () => {
    this.setState({ initLoading: true });
    const { history, location } = this.props;

    try {
      const widgetClientKey = process.env.WIDGET_CLIENT_KEY;
      const customerKey = this.props.logged ? this.props.user.user.id : ANONYMOUS;
      const paymentWidget = await loadPaymentWidget(widgetClientKey, customerKey);
      const { orderId } = this.props.match.params;

      // URL 쿼리 파라미터에서 토큰 가져오기
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');

      // 토큰이 있으면 쿼리 파라미터로 전달
      if (token) {
        await OrdersActions.getOrder(orderId, { token });
      } else {
        await OrdersActions.getOrder(orderId);
      }

      // 결제 정보를 로컬 스토리지에 저장 (결제 완료 후 주문서 생성용)
      const checkoutData = {
        checkoutPayload: this.props.order,
        logged: this.props.logged,
        timestamp: Date.now(),
        isOrderCreated: true,
      };
      localStorage.setItem('pendingCheckout', JSON.stringify(checkoutData));

      this.setState({ paymentWidget, initLoading: false });
    } catch (e) {
      console.error(e);
      this.setState({ initLoading: false });

      if (e.response) {
        // 401 에러: 인증 필요 - 로그인 페이지로 리다이렉트
        if (e.response.status === 401) {
          BaseActions.showToast({
            type: 'error',
            message: '로그인이 필요합니다.',
          });
          history.push(`/login?next=/orders/${this.props.match.params.orderId}`);
        }
        // 403 에러: 권한 없음 - 본인 주문이 아님
        else if (e.response.status === 403) {
          BaseActions.showToast({
            type: 'error',
            message: '접근 권한이 없습니다. 본인의 주문만 조회할 수 있습니다.',
          });
          history.push('/orders');
        }
        // 404 에러: 주문 없음
        else if (e.response.status === 404) {
          BaseActions.showToast({
            type: 'error',
            message: '주문을 찾을 수 없습니다.',
          });
          history.push('/orders');
        }
        // 기타 에러
        else {
          BaseActions.showToast({
            type: 'error',
            message: '주문 정보를 불러오는데 실패했습니다.',
          });
        }
      } else {
        BaseActions.showToast({
          type: 'error',
          message: '주문 정보를 불러오는데 실패했습니다.',
        });
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.paymentWidget !== this.state.paymentWidget) {
      this.renderPaymentWidget();
    }
  }

  componentDidMount() {
    this.initialize();
  }

  renderPaymentWidget = () => {
    const { paymentWidget } = this.state;
    if (!paymentWidget) return;

    const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
      '#payment-widget',
      { value: this.props.order.total.amount.raw },
      { variantKey: 'DEFAULT' },
    );

    const paymentAgreement = paymentWidget.renderAgreement('#agreement', {
      variantKey: 'AGREEMENT',
    });

    this.setState({ paymentMethodsWidget, paymentAgreement });
  };

  goInvoice = orderId => {
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    if (token) {
      this.props.history.push(`/orders/${orderId}/invoice?token=${token}`);
    } else {
      this.props.history.push(`/orders/${orderId}/invoice`);
    }
  };

  goOffer = orderId => {
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    if (token) {
      this.props.history.push(`/orders/${orderId}/offer?token=${token}`);
    } else {
      this.props.history.push(`/orders/${orderId}/offer`);
    }
  };

  goTax = orderId => {
    this.props.history.push(`/orders/${orderId}/tax`);
  };

  goRefund = orderId => {
    this.props.history.push(`/orders/${orderId}/refund`);
  };

  goNicePayReceipt = () => {
    window.open(
      `https://npg.nicepay.co.kr/issue/IssueLoader.do?TID=${this.props.order.payment.pg_tid}&type=0&InnerWin=Y`,
      'goNicePayReceipt',
      'width=750, height=700;',
    );
  };

  goTossPayReceipt = () => {
    window.open(
      this.props.order.tosspayments.receipt.url,
      'goTossPayReceipt',
      'width=500, height=700;',
    );
  };

  goPayJoaReceipt = () => {
    window.open(
      'https://www.kiwoompay.co.kr/main/payment_history',
      'goPayJoaReceipt',
      'width=500, height=700;',
    );
  };

  goBoltaTax = () => {
    const { order } = this.props;
    if (
      order.bolta &&
      order.bolta.data &&
      order.bolta.data.taxInvoiceUrl
    ) {
      const secret = order.documentaryEvidenceInfo && order.documentaryEvidenceInfo.businessNo 
        ? order.documentaryEvidenceInfo.businessNo 
        : '';
      let url = order.bolta.data.taxInvoiceUrl;
      
      if (secret) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}secret=${secret}`;
      }
      
      window.open(
        url,
        'goBoltaTax',
        'width=1400, height=1000;',
      );
    }
  };

  downloadDocument = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  onCancelOrder = () => {
    this.setState({
      cancelOrder: !this.state.cancelOrder,
    });
  };

  handleChange = (e: any) => {
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

  // eslint-disable-next-line consistent-return
  onCheckOut = async () => {
    // 토스페이 위젯 로딩 체크
    const { paymentWidget, paymentMethodsWidget, paymentAgreement } = this.state;

    if (!paymentWidget) {
      alert('결제 위젯이 로드되지 않았습니다. 다시 시도해주세요.');
      return false;
    }

    const selectedPaymentMethod = paymentMethodsWidget.getSelectedPaymentMethod();
    const isPaymentAgreement = paymentAgreement.getAgreementStatus().agreedRequiredTerms;

    if (!selectedPaymentMethod) return alert('결제수단을 선택해주세요.');
    if (!isPaymentAgreement) return alert('결제 서비스 이용 약관을 동의해야합니다.');

    let paymentMethod = 'default';
    if (selectedPaymentMethod.type === 'NORMAL') {
      if (selectedPaymentMethod.method === '카드') {
        paymentMethod = 'tosspayments';
      }
      if (selectedPaymentMethod.method === '가상계좌') {
        paymentMethod = 'virtual-bank';
      }
      if (selectedPaymentMethod.method === '계좌이체') {
        paymentMethod = 'quick-bank-transfer';
      }
      if (selectedPaymentMethod.method === '간편결제') {
        if (selectedPaymentMethod.easyPay.provider === '토스페이') {
          paymentMethod = 'tosspay';
        }
        if (selectedPaymentMethod.easyPay.provider === '네이버페이') {
          paymentMethod = 'naverpay';
        }
        if (selectedPaymentMethod.easyPay.provider === '카카오페이') {
          paymentMethod = 'kakaopay';
        }
      }
    } else if (selectedPaymentMethod.type === 'KEYIN') {
      if (selectedPaymentMethod.method === '카드') {
        paymentMethod = 'research-transfer';
      }
    } else if (selectedPaymentMethod.type === 'CUSTOM') {
      if (selectedPaymentMethod.paymentMethodKey === 'bank-transfer') {
        paymentMethod = 'bank-transfer';
      }
      if (selectedPaymentMethod.paymentMethodKey === 'post-transfer') {
        paymentMethod = 'post-transfer';
      }
    }

    // 무통장입금, 후불결제시 토스 프로세스가 없기 때문에 바로 성공 페이지로 이동
    if (selectedPaymentMethod.type === 'CUSTOM') {
      this.props.history.push(`/checkout-success?orderId=${this.props.order.id}&email=${this.props.order.customer.email}`);
    }

    const partNumber = this.props.order.items[0].product_price.part_number;
    const itemCount = this.props.order.items.length - 1;
    const orderName =
      this.props.order.items.length > 1 ? `${partNumber} 외 ${itemCount}종` : partNumber;

    // 토스페이 결제 모듈
    try {
      await paymentWidget.requestPayment({
        orderId: this.props.order.id,
        orderName,
        customerName: this.props.order.customer.name.full,
        customerEmail: this.props.order.customer.email,
        customerMobilePhone: this.props.order.customer.mobile.replace(/[^0-9]/g, ''),
        successUrl: `${window.location.origin}/checkout-success`,
        failUrl: `${window.location.origin}/checkout-fail`,
      });
    } catch (error) {
      BaseActions.showToast({
        type: 'error',
        message: error.message,
      });
    }
  };

  onCancel = async () => {
    const { orderId } = this.props.match.params;
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    await OrdersActions.cancelOrder(orderId, {
      reason: this.state.reason,
    });
    
    // token이 있으면 유지하면서 페이지 새로고침
    if (token) {
      window.location.href = `/orders/${orderId}?token=${token}`;
    } else {
      await this.initialize();
    }
  };

  onMarkAsNotReceived = async () => {
    const { orderId } = this.props.match.params;
    await OrdersActions.markAsNotReceived(orderId);
    await this.initialize();
  };

  onMarkAsReceived = async () => {
    const { orderId } = this.props.match.params;
    await OrdersActions.markAsReceived(orderId);
    await this.initialize();
  };

  changeRefundCancellation = () => {
    this.setState({
      isRefundCancellation: !this.state.isRefundCancellation,
    });
  };

  changeRefundCancellationReason = e => {
    this.setState({
      refundCancellationReason: e.target.value,
    });
  };

  onRefundCancellation = async refundId => {
    const { orderId } = this.props.match.params;
    await OrdersActions.cancelRefund(orderId, refundId, {
      reason: this.state.refundCancellationReason,
    });
    await this.initialize();
  };

  onPaymentMethod = event => {
    this.setState({
      paymentMethod: event.target.value,
    });
  };

  goProduct = async id => {
    if (!id) return;
    try {
      const result = await axios.get(`/products/url/${id}`);
      this.props.history.push(result.data);
    } catch (e) {
      console.error('goProduct error:', e);
    }
  };

  onCopyPurchaserLink = async orderId => {
    try {
      // 주문 정보에서 이메일 가져오기
      const { email } = this.props.order.customer;

      // 토큰 발급 API 호출
      const response = await axios.post(`/me/non-registered/orders/token`, {
        orderId,
        email,
      });

      const { token } = response.data;

      // 링크 생성 (email 파라미터 제외)
      const link = `${window.location.origin}/orders/${orderId}?token=${token}`;

      // 클립보드에 복사
      await navigator.clipboard.writeText(link);

      BaseActions.showToast({
        type: 'success',
        message: '구매담당자 링크가 클립보드에 복사되었습니다.',
      });
    } catch (e) {
      console.error(e);
      BaseActions.showToast({
        type: 'error',
        message: '링크 생성에 실패했습니다.',
      });
    }
  };

  render() {
    if (this.props.loading === undefined || this.props.loading)
      return <Circles color="blue" size="medium" text="Loading..." />;

    // 주문 데이터가 없으면 렌더링하지 않음
    if (!this.props.order.id) {
      return null;
    }

    return (
      <OrderDetailTemplate
        order={this.props.order}
        goProduct={this.goProduct}
        goInvoice={this.goInvoice}
        goOffer={this.goOffer}
        goTax={this.goTax}
        goRefund={this.goRefund}
        goNicePayReceipt={this.goNicePayReceipt}
        goTossPayReceipt={this.goTossPayReceipt}
        goPayJoaReceipt={this.goPayJoaReceipt}
        goBoltaTax={this.goBoltaTax}
        downloadDocument={this.downloadDocument}
        onCheckOut={this.onCheckOut}
        onCancelOrder={this.onCancelOrder}
        cancelOrder={this.state.cancelOrder}
        handleChange={this.handleChange}
        reason={this.state.reason}
        onCancel={this.onCancel}
        onMarkAsNotReceived={this.onMarkAsNotReceived}
        onMarkAsReceived={this.onMarkAsReceived}
        onPaymentMethod={this.onPaymentMethod}
        changeRefundCancellation={this.changeRefundCancellation}
        isRefundCancellation={this.state.isRefundCancellation}
        refundCancellationReason={this.state.refundCancellationReason}
        changeRefundCancellationReason={this.changeRefundCancellationReason}
        onRefundCancellation={this.onRefundCancellation}
        onCopyPurchaserLink={this.onCopyPurchaserLink}
      />
    );
  }
}

export default connect(
  ({ orders, user, pender }: State) => ({
    user,
    order: orders && orders.order,
    logged: !!user.user,
    loading: pender.pending['orders/GET_ORDER'],
  }),
  () => ({}),
)(withRouter(OrderDetailTemplateContainer));



// WEBPACK FOOTER //
// ./src/containers/order/OrderDetailTemplateContainer.js